import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServicePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPrices, setShowPrices] = useState(true);

  useEffect(() => {
    fetchService();
    fetchSettings();
  }, [slug]);

  const fetchService = async () => {
    try {
      const response = await axios.get(`${API}/services/${slug}`);
      setService(response.data);
    } catch (error) {
      console.error('Failed to fetch service:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/cms/site-settings`);
      setShowPrices(response.data.show_prices_on_services !== false);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const serviceDetails = {
    psychotherapy: {
      hero_image: 'https://images.unsplash.com/photo-1660831219257-dfc4f9bc843f?w=800',
      benefits: [
        'Develop deeper self-understanding',
        'Explore how past experiences shape your present',
        'Work through recurring emotional patterns',
        'Build capacity for more fulfilling relationships',
        'Connect with your own emotional truth and inner authority'
      ],
      helpful_for: [
        'Long-standing emotional distress',
        'Recurring patterns in relationships or work',
        'Feelings of emptiness or internal conflict',
        'Identity and belonging difficulties',
        'Processing difficult life experiences',
        'Personal growth and self-understanding'
      ],
      approach: 'Psychoanalytic psychotherapy is a reflective, relational approach. Together we explore not only your immediate difficulties, but also how earlier experiences continue to shape your current thoughts, emotions, and relationships. Sessions typically take place once weekly at the same time, offering consistency and containment for our work together.'
    },
    trauma: {
      hero_image: 'https://images.unsplash.com/photo-1763060869007-fc10548b0aae?w=800',
      benefits: [
        'Process traumatic experiences in a safe space',
        'Understand how trauma affects your present life',
        'Work through responses rooted in past harm',
        'Reconnect with fragmented parts of yourself',
        'Develop healthier ways of relating to yourself and others'
      ],
      helpful_for: [
        'Childhood trauma and neglect',
        'Domestic and relational abuse',
        'Coercive control',
        'Racial trauma and discrimination',
        'Complex PTSD symptoms',
        'Experiences of migration and displacement'
      ],
      approach: 'I offer specialised support for those who have experienced trauma. My approach is trauma-informed, meaning I understand how past harm shapes present responses. Together, we create a safe space to process these experiences at a pace that feels right for you, without retraumatisation.'
    },
    couples: {
      hero_image: 'https://images.pexels.com/photos/10679181/pexels-photo-10679181.jpeg?auto=compress&cs=tinysrgb&w=800',
      benefits: [
        'Improve communication and understanding',
        'Explore relationship dynamics together',
        'Work through conflicts constructively',
        'Rebuild trust and connection',
        'Understand how individual histories affect your relationship'
      ],
      helpful_for: [
        'Communication difficulties',
        'Trust and intimacy issues',
        'Recurring conflicts',
        'Life transitions affecting the relationship',
        'Processing past hurts within the relationship',
        'Deepening connection and understanding'
      ],
      approach: 'Couples therapy provides a space for partners to explore their relationship dynamics together. I help you understand how each person\'s history and emotional patterns interact within the relationship, and support you in developing healthier ways of relating to each other.'
    },
    bereavement: {
      hero_image: 'https://images.unsplash.com/photo-1622663180915-b3d92fbfb33a?w=800',
      benefits: [
        'Process grief at your own pace without judgement',
        'Explore the complex emotions that accompany loss',
        'Honour and remember the person or what you have lost',
        'Understand how grief connects to your history and relationships',
        'Find ways to carry your loss while continuing to live',
        'Navigate changes in identity and meaning after loss'
      ],
      helpful_for: [
        'Loss of a parent or family member',
        'Loss of a partner or spouse',
        'Loss of a child',
        'Pregnancy loss and miscarriage',
        'Loss of a close friend',
        'Pet loss and companion animal bereavement',
        'Anticipatory grief during terminal illness',
        'Complicated or prolonged grief',
        'Multiple losses or cumulative grief',
        'Unresolved grief from past losses',
        'Disenfranchised grief (losses not openly acknowledged)',
        'Loss of relationships, identity, or life as you knew it'
      ],
      approach: 'Grief is not a problem to be solved, but an experience to be lived through. There is no timeline for mourning, and no right way to grieve. In our sessions, I offer a steady, compassionate presence where all of your feelings can be brought—sadness, anger, guilt, relief, numbness, or even moments of unexpected joy. We explore not only the loss itself, but how it resonates with your history, your other relationships, and your sense of who you are. Together, we find ways to honour what has been lost while gently supporting you as you navigate life after loss.'
    },
    consultation: {
      hero_image: 'https://images.unsplash.com/photo-1710503701213-6b79941f5660?w=800',
      benefits: [
        'Discuss your needs and concerns',
        'Ask questions about the therapeutic process',
        'Get a sense of whether we are a good fit',
        'Understand how therapy might help you',
        'No commitment required'
      ],
      helpful_for: [
        'Anyone considering therapy',
        'Those unsure which type of therapy suits them',
        'People with questions about the process',
        'Anyone wanting to meet before committing'
      ],
      approach: 'The initial consultation is a chance for us to meet and for you to ask any questions you may have. We will discuss what brings you to therapy, and I can explain how I work. This is also an opportunity for both of us to sense whether we might be a good fit to work together.'
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h1 className="font-heading text-3xl mb-4">Service Not Found</h1>
          <Button onClick={() => navigate('/')}>Return Home</Button>
        </div>
      </div>
    );
  }

  const details = serviceDetails[slug] || serviceDetails.psychotherapy;

  return (
    <div className="min-h-screen pt-20" data-testid="service-page">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-accent/10" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <p className="text-sm font-medium tracking-wide uppercase text-accent">
                {service.category === 'couples' ? 'Couples' : service.category === 'consultation' ? 'Initial Meeting' : 'Individual'} Therapy
              </p>
              <h1 className="font-heading text-5xl md:text-6xl font-light tracking-tight" data-testid="service-title">
                {service.name}
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {service.description}
              </p>
              <div className="flex items-center gap-6 text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  <span>{service.duration_minutes} minutes</span>
                </div>
                {showPrices && (
                  <div className="text-lg font-medium text-primary">
                    £{service.price} per session
                  </div>
                )}
              </div>
              <div className="pt-4">
                <Button
                  size="lg"
                  className="rounded-full px-8 py-6 font-medium"
                  onClick={() => navigate('/book')}
                  data-testid="service-book-btn"
                >
                  {slug === 'consultation' ? 'Book Consultation' : 'Book a Session'}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:block"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-primary/10 rounded-3xl transform -rotate-3" />
                <img
                  src={details.hero_image}
                  alt={service.name}
                  className="relative rounded-2xl shadow-xl object-cover w-full aspect-[4/3]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl"
          >
            <h2 className="font-heading text-4xl font-light mb-6">The Approach</h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {details.approach}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-4xl font-light mb-8">What You Can Expect</h2>
              <ul className="space-y-4">
                {details.benefits.map((benefit, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-3"
                    data-testid={`benefit-${index}`}
                  >
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-4xl font-light mb-8">This May Help If You</h2>
              <div className="flex flex-wrap gap-3">
                {details.helpful_for.map((condition, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="px-4 py-2 bg-secondary rounded-full text-sm text-secondary-foreground"
                    data-testid={`condition-${index}`}
                  >
                    {condition}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Practical Info Section */}
      <section className="py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl font-light mb-8">Practical Information</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-card rounded-2xl p-6">
                <h3 className="font-heading text-xl mb-2">Duration</h3>
                <p className="text-muted-foreground">{service.duration_minutes} minutes per session</p>
              </div>
              {showPrices && (
                <div className="bg-card rounded-2xl p-6">
                  <h3 className="font-heading text-xl mb-2">Fee</h3>
                  <p className="text-muted-foreground">£{service.price} per session</p>
                </div>
              )}
              <div className="bg-card rounded-2xl p-6">
                <h3 className="font-heading text-xl mb-2">Frequency</h3>
                <p className="text-muted-foreground">
                  {slug === 'consultation' ? 'One-off meeting' : 'Weekly at a consistent time'}
                </p>
              </div>
              <div className="bg-card rounded-2xl p-6">
                <h3 className="font-heading text-xl mb-2">Format</h3>
                <p className="text-muted-foreground">In-person (London) or Online</p>
              </div>
              <div className="bg-card rounded-2xl p-6">
                <h3 className="font-heading text-xl mb-2">Languages</h3>
                <p className="text-muted-foreground">English, Portuguese, Spanish, French</p>
              </div>
              <div className="bg-card rounded-2xl p-6">
                <h3 className="font-heading text-xl mb-2">Cancellation</h3>
                <p className="text-muted-foreground">24 hours notice required</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl font-light text-primary-foreground mb-4">
              Ready to Begin?
            </h2>
            <p className="text-lg text-primary-foreground/80 max-w-2xl mx-auto mb-8">
              {slug === 'consultation' 
                ? 'Book your initial consultation to discuss your needs and see if we are a good fit.'
                : 'Start with an initial consultation where we can meet and discuss how I might help you.'}
            </p>
            <Button
              size="lg"
              variant="secondary"
              className="rounded-full px-8 py-6"
              onClick={() => navigate('/book')}
              data-testid="service-cta-book-btn"
            >
              {slug === 'consultation' 
                ? (showPrices ? 'Book Consultation · £50' : 'Book Consultation')
                : (showPrices ? 'Book Initial Consultation · £50' : 'Book Initial Consultation')
              }
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicePage;
